/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the block context menu.

   Modules R2 Phase 5: rebuilt on the kit ContextMenu component
   (`components/ui/context-menu.tsx`, Radix DropdownMenu underneath) —
   no more hand-rolled portal, outside-click listener or Escape
   handler; Radix owns positioning, focus trap, keyboard nav and
   dismissal. The item list is trimmed to what has no other path:
   the clipboard triad, Edit/Duplicate/Delete, Select with sub-items,
   Continuation and Skip at export, plus everything that only makes
   sense in context (variant, module insertion, Save as module, table
   commands). Level, Move up/down, inline marks and image width all
   moved to the toolbar / gutter / keyboard — see the repair-mode
   audit in the Phase 5 report; nothing on this list lost a home.

   Two absolutes of the design law meet here (DESIGN §4):
     · the clipboard triad — copy AND cut AND paste, each reachable
       from BOTH this menu and the platform shortcut
     · every feature reachable from visible UI: nothing the keyboard
       can do is keyboard-ONLY, because the user is an architect
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import {
  deleteSelectedBlocks,
  duplicateSelectedBlocks,
  toggleExcluded,
} from '../designer/commands';
import { TableMenuSection } from '../designer/TableMenuSection';
import { inTable } from '../designer/tableCommands';
import { rem } from '../lib/rem';
import { moduleStampLabel, type ModuleStamp } from '../../shared/spec-modules';
import {
  GROUP_MODULE_REFUSAL,
  groupSelectedBlocks,
  hasBlockSelection,
  pasteFromSystemClipboard,
  planGroup,
  runClipboardCommand,
  cutPickedAtom,
  selectSubtree,
  selectionHasGroup,
  toggleContinuation,
  ungroupSelectedBlocks,
} from './commands';
import { setBlockSelection } from './blockSelect';
import { useViewModeGuard } from './useViewModeGuard';
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '../components/ui/context-menu';

export interface SpecBlockMenuState {
  x: number;
  y: number;
  level: number;
  excluded: boolean;
  continuation: boolean;
  kind: 'text' | 'table' | 'image' | 'smart';
  /** Masters Ladder R3 — who owns this block in the active variant, and what the
   *  variant has already done to it. All three come from the classes the variant
   *  decorations painted, so the menu and the sheet can never disagree. */
  blockId: string;
  origin: 'core' | 'layer';
  overridden: boolean;
  ghost: boolean;
  /** Phase 3 — the office master changed this block and nobody has answered
   *  yet. Read from the same decoration class the sheet paints. */
  pendingUpdate: boolean;
  /** Phase 5 — set when this block arrived by INSERTING a module. Read from
   *  the node's own data, so the menu can only ever act on a real insertion. */
  moduleStamp: ModuleStamp | null;
  /** Resolved before the menu opens (probe, 80ms cap); false hides Paste. */
  clipboardHas?: boolean;
}

/** The variant's own block actions (R3 §2.3–2.4). Absent in the Office Master
 *  variant, where a block is just a block. */
export interface SpecVariantMenuProps {
  clientName: string;
  hide: () => void;
  restore: (blockId: string) => void;
  revert: (blockId: string) => void;
  /** Phase 3 — answer the office master's update on this block from here.
   *  `canDecide` is the specs.accept_updates gate; the items stay VISIBLE
   *  without it, disabled with the reason (DESIGN §4). */
  canDecide?: boolean;
  acceptUpdate?: (blockId: string) => void;
  rejectUpdate?: (blockId: string) => void;
}

/** "Save as module…" (Phase 4). A MODULE is a reusable block-group — never a
 *  variant. The action deliberately takes NO block id: it acts on the whole
 *  gutter selection, which is the same thing every other multi-block command
 *  in this Studio acts on (`selectedBlocks`). */
export interface SpecModuleMenuProps {
  /** modules.create. Without it the item stays VISIBLE but disabled, with the
   *  reason in its title (DESIGN §4) — never silently absent. */
  canCreate: boolean;
  saveAsModule: () => void;
  /** Phase 5 — act on the INSERTION the clicked block belongs to, as a unit. */
  selectInsertion?: (insertId: string, blockId: string) => void;
  removeInsertion?: (stamp: ModuleStamp) => void;
}

interface Props {
  editor: Editor;
  state: SpecBlockMenuState;
  canEdit: boolean;
  canDelete: boolean;
  variant?: SpecVariantMenuProps | null;
  moduleMenu?: SpecModuleMenuProps | null;
  /** MODULE MODE (Cohesion R1 Phase 1). This canvas is editing a module
   *  definition, not a spec: Skip at export never appears (excluded is
   *  stripped on save and would ride nothing anyway). The Part picker that
   *  used to live here moved to the Blocks toolbox (Cohesion R1 follow-up,
   *  Josh 2026-08-30 ruling) — a right-click menu is not where structural
   *  placement belongs. */
  moduleMode?: boolean;
  onClose: () => void;
}

const MENU_W = rem(248);

export function SpecBlockMenu({
  editor,
  state,
  canEdit,
  canDelete,
  variant,
  moduleMenu,
  moduleMode,
  onClose,
}: Props) {
  const dispatch = editor.view.dispatch.bind(editor.view);

  // View mode (Feel-test R2 item 4): edit-only items stay focusable, muted and
  // aria-disabled, and pressing one runs the guarded handler, which pulses the
  // Edit button. The reason rides the item's native title (a Hint tooltip
  // inside a Radix menu fights its focus handling). Items disabled for any
  // OTHER reason keep native disabled.
  const vm = useViewModeGuard();
  const ed = (other = false) => {
    const on = vm.inert && !other;
    return {
      disabled: !on && (other || !canEdit),
      cls: on ? ' sps-vm-inert' : '',
      aria: on ? (true as const) : undefined,
      title: (t: string) => (on ? vm.reason : t),
      run: <A extends unknown[]>(fn: (...a: A) => void) => (on ? vm.guard(fn) : fn),
    };
  };

  // The triad, two modes (Tranche 4 item 5). With a live gutter selection
  // each item runs the SAME command its shortcut does — whole blocks, one
  // undo step — by focusing the sheet and raising a real clipboard event, so
  // there is no second copy/cut implementation behind the menu. With no block
  // selection every line below behaves exactly as it did before.
  const blockMode = () => hasBlockSelection(editor.state);
  // A picked ATOM (a figure, a Related sections block) has no DOM text
  // selection the browser can copy or cut from a menu. It becomes a one-block
  // gutter selection first, so the menu runs the SAME block copy/cut its
  // shortcut does — variant gate and "a refused cut writes nothing" included.
  const atomAsBlock = () => {
    const sel = editor.state.selection;
    if (blockMode() || !(sel instanceof NodeSelection)) return;
    const id = (sel.node.attrs.blockId as string) || '';
    if (id) setBlockSelection(editor.state, dispatch, [id], id);
  };
  const copy = () => {
    atomAsBlock();
    return blockMode() ? runClipboardCommand(editor.view, 'copy') : document.execCommand('copy');
  };
  const cut = () => {
    atomAsBlock();
    if (!blockMode()) return document.execCommand('cut');
    return editor.state.selection instanceof NodeSelection
      ? cutPickedAtom(editor.view)
      : runClipboardCommand(editor.view, 'cut');
  };
  const paste = async () => {
    // Block payload first — the editor's own paste pipeline recognises it and
    // lands whole blocks; anything else falls through to the plain-text
    // insert this item has always done.
    if (await pasteFromSystemClipboard(editor.view)) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text) editor.chain().focus().insertContent(text).run();
    } catch {
      // Permission denied — Ctrl+V still pastes natively.
    }
  };

  // In a variant the office master's blocks are not this variant's to
  // delete — hiding is the operation that exists, and it leaves a ghost.
  const variantProtected = !!variant && state.origin !== 'layer';

  // Grouping (T3). Both items stay VISIBLE and carry their reason when they
  // cannot run (DESIGN §4) — never silently absent.
  const group = planGroup(editor.state);
  const hasGroup = selectionHasGroup(editor.state);
  const groupTitle =
    group.refusal === 'module'
      ? GROUP_MODULE_REFUSAL + ' — select the whole insertion, or leave it out'
      : group.refusal === 'few'
        ? 'Pick two or more blocks with the gutter grips first'
        : `Keep these ${group.ids.length} blocks together — they move, copy and delete as one`;

  // Paste only exists when there is something to paste. The system clipboard
  // is async, so the item shows until the probe says it is empty.
  const clipboardHas = state.clipboardHas !== false;

  const tableHere = inTable(editor.state);
  // A single block can never be grouped — hide Group there. A module-insertion
  // refusal keeps the item visible with its reason.

  const blockItems = () => (
    <>
      <ContextMenuItem
        className="dsg-menu-item"
        onSelect={() => editor.commands.focus()}
        title="Put the caret in this block and start typing"
      >
        Edit block<span className="dsg-menu-key">Enter</span>
      </ContextMenuItem>

      <ContextMenuSeparator className="dsg-menu-sep" />

      {/* ── Clipboard triad (ABSOLUTE — DESIGN §4) ── */}
      <ContextMenuItem className="dsg-menu-item" onSelect={copy}>
        Copy<span className="dsg-menu-key">Ctrl+C</span>
      </ContextMenuItem>
      <ContextMenuItem
        className={`dsg-menu-item${ed().cls}`}
        onSelect={ed().run(cut)}
        disabled={ed().disabled}
        aria-disabled={ed().aria}
        title={ed().title('')}
      >
        Cut<span className="dsg-menu-key">Ctrl+X</span>
      </ContextMenuItem>
      {clipboardHas && (
        <ContextMenuItem
          className={`dsg-menu-item${ed().cls}`}
          onSelect={ed().run(() => void paste())}
          disabled={ed().disabled}
          aria-disabled={ed().aria}
          title={ed().title('')}
        >
          Paste<span className="dsg-menu-key">Ctrl+V</span>
        </ContextMenuItem>
      )}

      <ContextMenuSeparator className="dsg-menu-sep" />

      <ContextMenuItem
        className={`dsg-menu-item${ed().cls}`}
        onSelect={ed().run(() => duplicateSelectedBlocks(editor.state, dispatch))}
        disabled={ed().disabled}
        aria-disabled={ed().aria}
        title={ed().title('Insert a copy right below')}
      >
        Duplicate<span className="dsg-menu-key">Ctrl+D</span>
      </ContextMenuItem>
      {hasGroup ? (
        <ContextMenuItem
          className={`dsg-menu-item${ed().cls}`}
          onSelect={ed().run(() => ungroupSelectedBlocks(editor.state, dispatch))}
          disabled={ed().disabled}
          aria-disabled={ed().aria}
          title={ed().title('Let these blocks travel on their own again')}
        >
          Ungroup<span className="dsg-menu-key">Ctrl+Shift+G</span>
        </ContextMenuItem>
      ) : (
        <ContextMenuItem
          className={`dsg-menu-item${ed(!!group.refusal).cls}`}
          onSelect={ed(!!group.refusal).run(() => groupSelectedBlocks(editor.state, dispatch))}
          disabled={ed(!!group.refusal).disabled}
          aria-disabled={ed(!!group.refusal).aria}
          title={ed(!!group.refusal).title(groupTitle)}
        >
          Group<span className="dsg-menu-key">Ctrl+G</span>
        </ContextMenuItem>
      )}
      {moduleMenu && (
        <ContextMenuItem
          className="dsg-menu-item"
          onSelect={() => moduleMenu.saveAsModule()}
          disabled={!moduleMenu.canCreate}
          title={
            moduleMenu.canCreate
              ? 'Save the selected blocks to the firm module library'
              : 'Saving modules needs the Save Modules privilege'
          }
        >
          Save as module…
        </ContextMenuItem>
      )}

      {/* ── The office master's update on this block — only while one waits ── */}
      {variant && state.pendingUpdate && variant.acceptUpdate && variant.rejectUpdate && (
        <>
          <ContextMenuItem
            className="dsg-menu-item"
            onSelect={() => variant.acceptUpdate?.(state.blockId)}
            disabled={!variant.canDecide}
            title={
              variant.canDecide
                ? "Take the office master's new text for this variant"
                : 'Ask an admin for “Accept / Reject Master Updates” to decide this'
            }
          >
            Accept update
          </ContextMenuItem>
          <ContextMenuItem
            className="dsg-menu-item"
            onSelect={() => variant.rejectUpdate?.(state.blockId)}
            disabled={!variant.canDecide}
            title={
              variant.canDecide
                ? `Keep ${variant.clientName}'s version as it stands`
                : 'Ask an admin for “Accept / Reject Master Updates” to decide this'
            }
          >
            Reject update
          </ContextMenuItem>
        </>
      )}

      <ContextMenuSub>
        <ContextMenuSubTrigger className="dsg-menu-item">
          More<span className="dsg-menu-key">▸</span>
        </ContextMenuSubTrigger>
        <ContextMenuSubContent className="dsg-menu sps-menu" style={{ width: MENU_W }}>
          <ContextMenuItem
            className="dsg-menu-item"
            onSelect={() => selectSubtree(editor.state, dispatch)}
            title="Select this block and everything nested under it"
          >
            Select with sub-items
          </ContextMenuItem>
          <ContextMenuCheckboxItem
            className={`dsg-menu-item${state.continuation ? ' is-current' : ''}${ed().cls}`}
            checked={state.continuation}
            onCheckedChange={ed().run(() => toggleContinuation(editor.state, dispatch))}
            disabled={ed().disabled}
            aria-disabled={ed().aria}
            title={ed().title('No number — flows under the previous paragraph.')}
          >
            Continuation<span className="dsg-menu-key">Ctrl+Shift+C</span>
          </ContextMenuCheckboxItem>
          {/* Skip at export means nothing inside a module definition — it never
              reaches a spec (excluded is stripped on save), so the item does
              not exist here rather than offering something that does nothing. */}
          {!moduleMode && (
            <ContextMenuCheckboxItem
              className={`dsg-menu-item${state.excluded ? ' is-current' : ''}${ed().cls}`}
              checked={state.excluded}
              onCheckedChange={ed().run(() => toggleExcluded(editor.state, dispatch))}
              disabled={ed().disabled}
              aria-disabled={ed().aria}
              title={ed().title('The block stays here but is skipped when the spec is exported')}
            >
              {state.excluded ? 'Include in export' : 'Skip at export'}
            </ContextMenuCheckboxItem>
          )}
          {variant && state.overridden && (
            <ContextMenuItem
              className={`dsg-menu-item${ed().cls}`}
              onSelect={ed().run(() => variant.revert(state.blockId))}
              disabled={ed().disabled}
              aria-disabled={ed().aria}
              title={ed().title(
                "Put the office master's words back. Ctrl+Z brings your version back."
              )}
            >
              Revert to core
            </ContextMenuItem>
          )}
          {variant && state.ghost && (
            <ContextMenuItem
              className={`dsg-menu-item${ed().cls}`}
              onSelect={ed().run(() => variant.restore(state.blockId))}
              disabled={ed().disabled}
              aria-disabled={ed().aria}
              title={ed().title('Bring this block back for this variant')}
            >
              Restore in this variant
            </ContextMenuItem>
          )}
          {/* The insertion this block belongs to, as a unit — only when this
              block came from a module (Phase 5). */}
          {state.moduleStamp && moduleMenu?.selectInsertion && moduleMenu?.removeInsertion && (
            <>
              <ContextMenuSeparator className="dsg-menu-sep" />
              <ContextMenuLabel className="sps-menu-label">
                {moduleStampLabel(state.moduleStamp)}
              </ContextMenuLabel>
              <ContextMenuItem
                className="dsg-menu-item"
                onSelect={() =>
                  moduleMenu.selectInsertion?.(state.moduleStamp!.insertId, state.blockId)
                }
                title="Select every block this insertion brought in"
              >
                Select insertion
              </ContextMenuItem>
              <ContextMenuItem
                className={`dsg-menu-item is-danger${ed().cls}`}
                onSelect={ed().run(() => moduleMenu.removeInsertion?.(state.moduleStamp!))}
                disabled={ed().disabled}
                aria-disabled={ed().aria}
                title={ed().title(
                  'Delete every block this insertion brought in — Ctrl+Z brings them back'
                )}
              >
                Remove insertion
              </ContextMenuItem>
            </>
          )}
        </ContextMenuSubContent>
      </ContextMenuSub>

      {/* Exactly one destructive row, at the bottom. In a variant the office
          master's block is not deletable — hiding is (§2.4), reversible; a
          hidden block offers Restore (under More) instead. */}
      {variantProtected && !state.ghost && variant && (
        <>
          <ContextMenuSeparator className="dsg-menu-sep" />
          <ContextMenuItem
            className={`dsg-menu-item${ed().cls}`}
            onSelect={ed().run(() => variant.hide())}
            disabled={ed().disabled}
            aria-disabled={ed().aria}
            title={ed().title(
              "The office master's block cannot be deleted from a variant — hide it instead. It stays visible here as a dashed stub."
            )}
          >
            Hide in this variant<span className="dsg-menu-key">Del</span>
          </ContextMenuItem>
        </>
      )}
      {!variantProtected && canDelete && (
        <>
          <ContextMenuSeparator className="dsg-menu-sep" />
          <ContextMenuItem
            className={`dsg-menu-item is-danger${ed().cls}`}
            onSelect={ed().run(() => deleteSelectedBlocks(editor.state, dispatch))}
            disabled={ed().disabled}
            aria-disabled={ed().aria}
            title={ed().title('Delete this block — Ctrl+Z brings it back')}
          >
            Delete block<span className="dsg-menu-key">Del</span>
          </ContextMenuItem>
        </>
      )}
    </>
  );

  return (
    <ContextMenu open onOpenChange={(next) => !next && onClose()}>
      {/* No real element to anchor a right-click to — an invisible fixed
          point at the click coordinates is the Popper reference, exactly the
          documented pattern for `components/ui/context-menu.tsx`.

          Portalled straight to `document.body`: the canvas lives inside a
          FormaDock (dockview) panel, and dockview's own panel/tab chrome
          carries `transform: translate3d(...)` for GPU layering. A CSS
          transform on ANY ancestor makes `position: fixed` descendants
          anchor to THAT ancestor's box instead of the viewport — so without
          the portal this span renders at `state.x/y` relative to the dock
          panel, not the screen, and Radix (reading its real, wrong,
          getBoundingClientRect) opens the menu off from the cursor by
          however far the panel sits from the true top-left. Rendering
          outside the transformed subtree is what makes `position: fixed`
          mean what it says. (The whole Trigger is portalled, not just the
          span, so Radix's `asChild` Slot still clones a real element —
          Root's React context reaches it fine; portals don't affect
          context, only DOM placement.) */}
      {createPortal(
        <ContextMenuTrigger asChild>
          <span
            aria-hidden="true"
            style={{
              position: 'fixed',
              left: state.x,
              top: state.y,
              width: rem(1),
              height: rem(1),
              pointerEvents: 'none',
            }}
          />
        </ContextMenuTrigger>,
        document.body
      )}
      <ContextMenuContent
        className="dsg-menu sps-menu"
        // Radix publishes the room actually left on screen as this custom
        // property (Popper collision detection) — capping to it (never more
        // than the old hand-rolled menu's 520px ceiling) is what keeps a long
        // menu SCROLLABLE instead of clipped by the window edge.
        style={{
          width: MENU_W,
          maxHeight: `min(${rem(520)}, var(--radix-dropdown-menu-content-available-height, ${rem(520)}))`,
        }}
        align="start"
        sideOffset={0}
        collisionPadding={8}
        aria-label="Block actions"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* One quiet eyebrow, variants only — not an item. */}
        {variant && (
          <ContextMenuLabel className="sps-menu-label">
            From {state.origin === 'layer' ? variant.clientName : 'Office master'}
            {state.overridden ? ' · overridden' : ''}
            {state.ghost ? ' · hidden' : ''}
          </ContextMenuLabel>
        )}

        {/* In a table the table commands ARE the context; the block's own
            actions step down into one submenu. */}
        {tableHere ? (
          <>
            <TableMenuSection editor={editor} canEdit={canEdit} onClose={onClose} radixItems bare />
            <ContextMenuSeparator className="dsg-menu-sep" />
            <ContextMenuSub>
              <ContextMenuSubTrigger className="dsg-menu-item">
                Block<span className="dsg-menu-key">▸</span>
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="dsg-menu sps-menu" style={{ width: MENU_W }}>
                {blockItems()}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        ) : (
          blockItems()
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
